<template>
  <!-- Header with rounded bottom -->
  <div class="bg-bg">
    <div
      :class="
        (props.theme
          ? `from-gradient-${props.theme} to-gradient-${props.theme}`
          : 'from-[#4479AF] to-[#6AAEA3]') + (props.imageType != 'full' ? ' p-6' : '')
      "
      class="header relative mb-4 w-full overflow-hidden bg-gradient-to-r pt-[max(2.5rem,env(safe-area-inset-top))]"
    >
      <!-- eslint-disable-next-line vue/no-deprecated-slot-attribute -->
      <ion-buttons v-if="back" slot="start" class="fixed left-6 text-white md:left-28">
        <ion-back-button :default-href="back"></ion-back-button>
      </ion-buttons>
      <img
        v-if="props.theme == 'amazing'"
        src="/amazing.png"
        class="absolute top-4 bottom-4 h-4 w-4"
      />
      <!-- eslint-disable-next-line vue/no-deprecated-slot-attribute -->
      <ion-buttons v-if="!disableEditMode" slot="start" class="absolute right-6 text-white">
        <directus-image
          v-if="!props.editMode"
          :src="ICON_PEN"
          class="w-16 cursor-pointer"
          @click="emit('toggleEditMode')"
        />
        <directus-image
          v-if="props.editMode"
          :src="ICON_SAVE"
          class="w-16 cursor-pointer"
          @click="emit('toggleEditMode')"
        />
      </ion-buttons>
      <!-- Title -->
      <div
        :contenteditable="editMode"
        :textContent="localTitle"
        class="font-fredoka font-outline-1 relative z-10 text-center text-3xl font-bold text-white drop-shadow outline-none select-text"
        :class="{ 'cursor-text': isEditable, 'cursor-default': !isEditable, shake: props.editMode }"
        @blur="
          (e) => {
            const newText = (e.target as HTMLElement).textContent || '';
            localTitle = newText;
            emit('input', newText);
          }
        "
      />
      <div
        v-if="subtitle"
        class="font-fredoka font-outline-1 relative z-10 text-center text-xl font-bold text-white drop-shadow outline-none select-text"
      >
        {{ subtitle }}
      </div>
      <!-- Illustration -->
      <div class="mt-4 flex h-40 justify-center">
        <template v-if="imageType === 'component'">
          <component
            :is="props.image"
            class="-mt-4 h-[200px] w-[200px] text-white opacity-70"
            :class="imageClass"
          />
        </template>
        <template v-if="imageType === 'emoji'">
          <div class="-mt-14 text-[200px]" :class="imageClass">{{ props.image }}</div>
        </template>
        <template v-if="imageType === 'full'">
          <div class="absolute top-0 left-0 h-[100%] w-[100%]">
            <directus-image
              :src="props.image"
              :class="
                props.editMode && props.database
                  ? 'shake cursor-pointer'
                  : props.image == '837dea20-1b1a-456b-9fe7-f1f8b4cde476'
                    ? 'shake'
                    : ''
              "
              class="h-[100%] w-[100%] object-cover object-[50%_40%]"
              @click="toggleImageSelection()"
            />
          </div>
        </template>
        <template v-if="imageType === 'character'">
          <directus-image
            :src="props.image"
            :class="
              props.editMode && props.database
                ? 'shake cursor-pointer'
                : props.image == '837dea20-1b1a-456b-9fe7-f1f8b4cde476'
                  ? 'shake'
                  : ''
            "
            class="relative bottom-5 z-0 h-[500px] max-h-[500px] w-[500px] max-w-[500px]"
            @click="toggleImageSelection()"
          />
        </template>
        <template v-if="imageType === 'icon'">
          <directus-image
            :src="props.image"
            :class="props.editMode && props.database ? 'shake cursor-pointer' : ''"
            class="relative bottom-5 z-0 h-[300px] max-h-[300px] w-[300px] max-w-[300px]"
            @click="toggleImageSelection()"
          />
        </template>
      </div>
      <slot />
    </div>
    <div v-if="isImagePickEnabled && props.database" class="flex justify-center">
      <div
        class="bg-bg border-bg-neutral absolute z-20 flex max-w-[80%] flex-wrap rounded-2xl border-4"
      >
        <directus-image
          v-for="icon in props.database"
          :key="icon.id"
          :src="icon.id"
          class="w-16 cursor-pointer"
          @click="pickImage(icon.id)"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import DirectusImage from './DirectusImage.vue';
import { IonBackButton, IonButtons } from '@ionic/vue';
import { type Ref, ref, useAttrs, watchEffect } from 'vue';
import { ICON_PEN, ICON_SAVE } from '../services/Directus.ts';
const props = defineProps([
  'image',
  'title',
  'subtitle',
  'imageType',
  'back',
  'database',
  'editMode',
  'theme',
  'imageClass',
  'disableEditMode',
]);
const emit = defineEmits(['input', 'pickIcon', 'toggleEditMode']);
const attrs = useAttrs();

const isEditable = 'onInput' in attrs;
const isImagePickEnabled: Ref<boolean> = ref<boolean>(false);
const localTitle = ref(props.title);

watchEffect(() => {
  localTitle.value = props.title;
});

function toggleImageSelection() {
  if (!props.editMode) {
    return;
  }
  isImagePickEnabled.value = !isImagePickEnabled.value;
}

function pickImage(id: string) {
  emit('pickIcon', id);
  isImagePickEnabled.value = false;
}
</script>

<style scoped>
.header {
  border-bottom-left-radius: 100% 50px;
  border-bottom-right-radius: 100% 50px;
}
</style>
